import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useDebounce from '../../hooks/useDebounce';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useAlert } from '../../context/alert';
import { useCategoriesInfiniteQuery } from '../../hooks/useCategoriesQuery';
import { CategoryResponse } from '../../types/api/categories';
import { CategoryView } from './components/CategoryView';
import { Placeholder, createErrorPlaceholder } from '../basic';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { pageSize } from '../../services/settings';
import TableActionsBanner from '../common/TableActionsBanner';
import { TransferAdsModal } from './components/TransferAdsModal';
import { CategoryModal } from './components/CategoryModal';
import { Stack, TextInput, Button, SkeletonBox } from '@primer/react';
import { SearchIcon, XCircleFillIcon, XCircleIcon, XIcon, PlusIcon } from '@primer/octicons-react'
import { deleteCategory } from '../../services/categories';

export default function CategoriesList() {

    const navigate = useNavigate();
    const listRef = useRef<HTMLDivElement | null>(null);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const [selectedCategory, setSelectedCategory] = useState<CategoryResponse | undefined>(undefined);
    const [openTransferModal, setOpenTransferModal] = React.useState(false)
    const [openCategoryModal, setOpenCategoryModal] = React.useState(false)

    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)


    const {
        isLoading: isLoadingAds,
        data: sectionsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useCategoriesInfiniteQuery(
        debouncedSearchQuery, // search
        pageSize, //limit
        {
            enabled: true
        }
    );

    const { mutate: deleteCategoryRequest } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => deleteCategory({ ids: [id], replaceBy: null }),
        onSuccess: (data) => {
            categoryDidDeleted(false)
        }
    });

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    useEffect(() => {
    }, [sectionsData]);

    const handleScroll = useInfiniteScroll(
        listRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        sectionsData
    );

    function createConfirmDeleteAlertProps(category: CategoryResponse) {
        return {
            title: 'Точно хотите удалить раздел?',
            count: undefined,
            buttons: [
                <Button key="no-button" variant="default" onClick={dismissConfirmBlockAlert}>
                    Нет
                </Button>,
                <Button key="yes-button" variant="danger" onClick={() => deleteOrShowTransfer(category)}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const dismissConfirmBlockAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
    }, [actionsAlertId]);

    const openCategoryPage = (category: CategoryResponse) => {
        navigate('/catalogs/?id=' + category.id)
    }
    const createNewCategory = () => {
        setOpenCategoryModal(true)
    }

    const editCategory = (category: CategoryResponse) => {
        setSelectedCategory(category)
        setOpenCategoryModal(true)
    }

    const deleteCategoryAction = (category: CategoryResponse) => {
        showDeleteCategoryAlert(category)
    }

    const categoryDismissed = () => {
        setSelectedCategory(undefined)
        setOpenCategoryModal(false)
    }

    const categorySaved = () => {
        setSelectedCategory(undefined)
        setOpenCategoryModal(false)
        refetch()
    }

    const deleteOrShowTransfer = (category: CategoryResponse) => {
        if (category.adsCount === 0) {
            deleteCategoryRequest({ id: category.id })
        } else {
            openTransferAdsModal(category)
        }
    }
    const openTransferAdsModal = (category: CategoryResponse) => {
        dismissConfirmBlockAlert()
        setSelectedCategory(category)
        setOpenTransferModal(true)
    }

    const showDeleteCategoryAlert = useCallback((category: CategoryResponse) => {
        dismissConfirmBlockAlert()

        let props = createConfirmDeleteAlertProps(category)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );
        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId]);

    const transferModalDidClosed = () => {
        setOpenTransferModal(false)
        setSelectedCategory(undefined)
    }

    const categoryDidDeleted = (withTransfer: boolean) => {
        transferModalDidClosed()
        refetch()
        dismissConfirmBlockAlert()
        setInfo(withTransfer ? 'Раздел удален и объявления перенесены' : 'Раздел удален')
    }

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const emptyListDescription = `По вашему запросу не найдено
    подходящих разделов`;

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <div></div>
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />
                        <Button
                            variant="default"
                            size="small"
                            onClick={createNewCategory}
                            leadingVisual={PlusIcon}
                        >
                            Создать раздел
                        </Button>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>

                    {selectedCategory && openTransferModal && (<TransferAdsModal
                        isOpen={openTransferModal}
                        category={selectedCategory}
                        onDidDeleted={() => categoryDidDeleted(true)}
                        onClose={transferModalDidClosed}
                    />)}
                    {openCategoryModal && (<CategoryModal
                        isOpen={openCategoryModal}
                        category={selectedCategory}
                        onDidSaved={categoryDismissed}
                        onClose={categorySaved}
                    />)}
                    {!isLoadingAds && !isFetchingNextPage && (sectionsData?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    <div
                        ref={listRef}
                        onScroll={handleScroll}
                        className="mx-auto max-w-screen-xl w-full overflow-y-auto"
                        style={{
                            height: 'calc(100vh - 120px)'
                        }}
                    >
                        {sectionsData?.pages.map((page, i) => (
                            <React.Fragment key={i}>
                                {page.list.map((e: CategoryResponse, index) => (
                                    <CategoryView
                                        key={e.id}
                                        category={e}
                                        isAllowEdit={true}
                                        isForForum={false}
                                        onCategoryClick={() => openCategoryPage(e)}
                                        onEditClick={() => editCategory(e)}
                                        onDeleteClick={() => deleteCategoryAction(e)}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                        <div className='block h-20'></div>

                    </div>
                    {/*Индикатор загрузки*/}
                    {(isFetchingNextPage || isLoadingAds) &&
                        <div className="max-h-screen overflow-y-auto">
                            <SkeletonBox height="4rem" />
                        </div>
                    }
                </Stack>
            }
        ></RegularPageLayout>

    )
}